import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Download, Trash2, AlertTriangle, Loader2, Wand2, Sparkles } from 'lucide-react';
import { toast } from '../ui/use-toast';
import { IdeogramPrompt } from './types';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { getPrompts } from '../../services/ideogramService';
import { CreateDialog } from './CreateDialog';

interface PromptsProps {
  onStartGenerating?: () => void;
}

export const Prompts: React.FC<PromptsProps> = ({ onStartGenerating }) => {
  const [prompts, setPrompts] = useState<IdeogramPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<IdeogramPrompt | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createDialogPrompt, setCreateDialogPrompt] = useState('');
  const [isRemixDialogOpen, setIsRemixDialogOpen] = useState(false);

  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const data = await getPrompts();
        setPrompts(data.prompts);
      } catch (error) {
        console.error('Error fetching prompts:', error);
        toast({
          title: "Error",
          description: "Failed to fetch prompts",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrompts();
  }, []);

  const handleDownload = (imageUrl: string) => {
    window.open(imageUrl, '_blank');
  };

  const handleDelete = async (imageUrl: string) => {
    try {
      setIsDeleting(true);
      // Extract the file path from the Firebase Storage URL
      const storage = getStorage();
      const urlPath = decodeURIComponent(imageUrl.split('/o/')[1].split('?')[0]);
      const imageRef = ref(storage, urlPath);
      
      // Delete the file from Firebase Storage
      await deleteObject(imageRef);
      
      // Close dialogs and refresh the prompts
      setIsDeleteDialogOpen(false);
      setSelectedImage(null);
      
      // Refresh prompts
      const data = await getPrompts();
      setPrompts(data.prompts);
      
      toast({
        title: "Success",
        description: "Image deleted successfully.",
        variant: "success",
      });
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: "Error",
        description: "Failed to delete image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const GeneratingImage = () => (
    <div className="w-full h-48 bg-gray-100 rounded-lg flex flex-col items-center justify-center space-y-2">
      <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      <p className="text-sm text-gray-500">Generating image...</p>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-8">Prompts History</h1>
      
      <div className="space-y-4">
        {prompts.map((prompt, index) => (
          <div key={index} className="border rounded-lg overflow-hidden bg-white">
            <div className="grid grid-cols-2 gap-4">
              {/* Left Column - Information */}
              <div className="p-4">
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-900">
                    {prompt.prompt}
                  </p>
                  <div className="flex flex-wrap gap-3 text-xs">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full">
                      {prompt.style}
                    </span>
                    <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full">
                      {prompt.aspectRatio.replace('ASPECT_', '').replace('_', ':')}
                    </span>
                    <span className="px-2 py-1 bg-gray-50 text-gray-600 rounded-full">
                      {new Date(prompt.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCreateDialogPrompt(prompt.prompt);
                        setIsCreateDialogOpen(true);
                      }}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Wand2 className="mr-1 h-4 w-4" />
                      Use Prompt
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedImage(prompt);
                        setIsRemixDialogOpen(true);
                      }}
                      className="text-purple-600 hover:text-purple-700"
                    >
                      <Sparkles className="mr-1 h-4 w-4" />
                      Remix
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => prompt.imageUrl && handleDownload(prompt.imageUrl)}
                      className="text-gray-600"
                    >
                      <Download className="mr-1 h-4 w-4" />
                      Download
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedImage(prompt);
                        setIsDeleteDialogOpen(true);
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>

              {/* Right Column - Image */}
              <div className="p-4 bg-gray-50 flex items-center justify-center" style={{ minHeight: '300px' }}>
                {prompt.status === 'generating' || !prompt.imageUrl ? (
                  <GeneratingImage />
                ) : (
                  <img
                    src={prompt.imageUrl}
                    alt={prompt.prompt}
                    className="max-w-full max-h-[280px] w-auto h-auto object-contain rounded-lg"
                    onClick={() => setSelectedImage(prompt)}
                    style={{ cursor: 'pointer' }}
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.style.display = 'none';
                      img.parentElement?.appendChild(
                        (() => {
                          const div = document.createElement('div');
                          div.className = 'w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center';
                          div.innerHTML = '<p class="text-sm text-gray-500">Failed to load image</p>';
                          return div;
                        })()
                      );
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Image Details Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-3xl h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Image Details</DialogTitle>
          </DialogHeader>
          <div className="flex-1 flex flex-col min-h-0">
            {/* Image container */}
            <div className="flex-1 min-h-0 flex items-center justify-center aspect-video">
              <img
                src={selectedImage?.imageUrl}
                alt={selectedImage?.prompt}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            </div>
            {/* Info section */}
            <div className="space-y-4 flex-shrink-0 mt-4">
              <div className="space-y-2">
                <div className="flex gap-4">
                  <div>
                    <h3 className="font-semibold text-sm">Style</h3>
                    <p className="text-sm text-gray-600">{selectedImage?.style}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">Aspect Ratio</h3>
                    <p className="text-sm text-gray-600">
                      {selectedImage?.aspectRatio.replace('ASPECT_', '').replace('_', ':')}
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold">Prompt</h3>
                  <p className="text-sm text-gray-600">{selectedImage?.prompt}</p>
                </div>
              </div>
              {/* Actions */}
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (selectedImage?.prompt) {
                      setCreateDialogPrompt(selectedImage.prompt);
                      setIsCreateDialogOpen(true);
                      setSelectedImage(null);
                    }
                  }}
                >
                  <Wand2 className="mr-2 h-4 w-4" />
                  Use Prompt
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedImage(selectedImage);
                    setIsRemixDialogOpen(true);
                  }}
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Remix
                </Button>
                <Button
                  variant="outline"
                  onClick={() => selectedImage?.imageUrl && handleDownload(selectedImage.imageUrl)}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setIsDeleteDialogOpen(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <CreateDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onStartGenerating={onStartGenerating}
        initialPrompt={createDialogPrompt}
      />

      {/* Remix Dialog */}
      <CreateDialog
        open={isRemixDialogOpen}
        onOpenChange={setIsRemixDialogOpen}
        onStartGenerating={onStartGenerating}
        initialPrompt={selectedImage?.prompt}
        isRemix={true}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this image? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="gap-3 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedImage?.imageUrl && handleDelete(selectedImage.imageUrl)}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
